/**
 * Install-nudge module — Phase 6 sub-commit 7 (13 Jun 2026).
 *
 * Powers the dismissible PWA install banner on MePage (see
 * src/components/InstallNudge.tsx). This module owns three concerns:
 *
 *   1. Per-user dismissal state — when a guest declines the nudge or
 *      successfully installs, we record that fact in localStorage so
 *      the banner doesn't reappear on every MePage render. Identity-
 *      scoped key `ti:installNudge:{userId}` mirrors the discipline
 *      applied to chat history (10 Jun 2026 fix) and outbox: account
 *      A and account B on the same device look at different keys, so
 *      cross-account "Bob's dismissal silenced Alice's banner" is
 *      structurally impossible.
 *
 *   2. Install-state detection — `isAppInstalled()` returns true when
 *      the PWA is running in standalone display-mode (added to home
 *      screen, launched from the icon). Banner never renders when
 *      installed, regardless of dismissal state — the action is moot.
 *      Read once on mount; display-mode is stable within a session.
 *
 *   3. iOS detection — UA sniff for `iPhone|iPad|iPod`, plus the
 *      iPadOS-Desktop-Mode edge case where iPad reports a Mac UA
 *      but exposes touch via `navigator.maxTouchPoints > 0`. iOS
 *      Safari intentionally never fires `beforeinstallprompt`, so
 *      we can't feature-detect — UA sniff is the one legitimate
 *      use case. Banner on iOS opens a modal explaining the
 *      Share → Add to Home Screen flow instead of triggering a
 *      native prompt.
 *
 *   4. Singleton `beforeinstallprompt` capture — the browser fires
 *      this event exactly once per page load, well before
 *      InstallNudge typically mounts (the event fires when the
 *      browser determines the PWA is installable, which can happen
 *      during JS bootstrap before React hydrates). Without
 *      module-level capture, the event would be missed and the
 *      banner would never get a deferred-prompt to invoke. The
 *      cached event also survives MePage unmount/remount (e.g.
 *      navigating to /settings and back) because the browser will
 *      not re-fire the event in the same session.
 *
 * --------------------------------------------------------------------
 * Why storage helpers live here and not in `storage.ts`
 * --------------------------------------------------------------------
 *
 * `storage.ts` is the centralised localStorage primitives module
 * (`readValue` / `writeValue` / `removeValue`) and hosts the
 * persistence helpers for app-level state that crosses feature
 * boundaries (personalPage, chatHistory, memoryOffered, startersDismissed,
 * pendingRef). The install-nudge dismissal flag is install-feature-
 * internal — no other module reads or writes it. Keeping the helpers
 * here means the module is the single source of truth for "everything
 * the install nudge needs to know," and storage.ts stays focused on
 * cross-cutting state. The helpers still go through `readValue` /
 * `writeValue` / `removeValue` rather than touching `window.localStorage`
 * directly, so the namespace, availability check, and Zod-validation
 * discipline applied to every other persisted value applies here too.
 */

import { z } from 'zod';
import { readValue, writeValue, removeValue } from './storage';

// ─── Types ───────────────────────────────────────────────────────────

/**
 * Type signature for the `beforeinstallprompt` event. Non-standard
 * (not in TS DOM lib), so we declare the shape we use here. Browsers
 * that support PWA installation (Chromium-based desktop, Android
 * Chrome, Edge, Samsung Internet) fire this on the window when they
 * determine the page is installable.
 *
 * `prompt()` triggers the native install dialog. Calling it without
 * a prior user gesture (button click) is blocked by most browsers.
 *
 * `userChoice` resolves once the user accepts or dismisses the native
 * dialog. We record `dismissedReason: 'installed'` only when outcome
 * is `'accepted'`.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// ─── Dismissal state (per-user) ──────────────────────────────────────

/**
 * Persisted dismissal state. Recorded when the guest either
 *
 *   - Successfully installs (`dismissedReason: 'installed'`) — the
 *     banner never appears again for this user, and on subsequent
 *     visits the app launches in standalone display-mode so the
 *     `isAppInstalled()` guard short-circuits before even reading
 *     this state.
 *
 *   - Explicitly declines via the "Not now" button
 *     (`dismissedReason: 'declined'`). Banner never appears again
 *     for this user on this device. Respectful: once dismissed,
 *     no second nudge. A premium product asks once.
 *
 * `dismissedAt` is informational (analytics / debugging); the mere
 * existence of a stored value is what gates the banner.
 */
const installNudgeStateSchema = z.object({
  dismissedAt: z.string().datetime(),
  dismissedReason: z.enum(['declined', 'installed']),
});

export type InstallNudgeState = z.infer<typeof installNudgeStateSchema>;

/**
 * Compose the per-user storage key name. Returns null when no userId
 * is supplied — same null-on-missing discipline as `chatHistoryKeyName`
 * in storage.ts. The null causes the caller (read/write/clear helpers)
 * to silently no-op rather than falling back to a shared key, making
 * cross-account leakage structurally impossible.
 *
 * The `string | null | undefined` parameter accepts Clerk's
 * `useAuth().userId` shape (which is `string | null | undefined`
 * during the loading window) without forcing coercion at call sites.
 */
function nudgeKeyName(
  userId: string | null | undefined,
): string | null {
  if (!userId) return null;
  return `installNudge:${userId}`;
}

export function readInstallNudge(
  userId: string | null | undefined,
): InstallNudgeState | null {
  const name = nudgeKeyName(userId);
  if (!name) return null;
  return readValue(name, installNudgeStateSchema);
}

export function writeInstallNudge(
  userId: string | null | undefined,
  state: InstallNudgeState,
): void {
  const name = nudgeKeyName(userId);
  if (!name) return;
  writeValue(name, state);
}

export function clearInstallNudge(
  userId: string | null | undefined,
): void {
  const name = nudgeKeyName(userId);
  if (!name) return;
  removeValue(name);
}

// ─── Platform / install detection ────────────────────────────────────

/**
 * Returns true when the PWA is currently running in standalone
 * display-mode — i.e. launched from the home screen icon rather
 * than via a browser tab. The `display-mode: standalone` media
 * query is the standard cross-platform check (Chrome, Safari, Edge,
 * Samsung Internet all support it for installed PWAs).
 *
 * Used as the first gate in InstallNudge — when the app IS installed,
 * the banner is moot, so we render nothing regardless of dismissal
 * state. Defensive against environments without `window.matchMedia`
 * (vanishingly rare on modern browsers but defended for jsdom and
 * older WebViews).
 */
export function isAppInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * Returns true on iPhone, iPad, or iPod — including iPadOS in
 * "Request Desktop Website" mode, where Safari reports a Mac user
 * agent. macOS reports `navigator.maxTouchPoints === 0`; iPad in any
 * mode reports > 0 (typically 5), which is the discriminator.
 *
 * Used to switch InstallNudge between the native-prompt path
 * (Android / desktop Chromium) and the iOS modal-with-instructions
 * path. iOS Safari intentionally never fires `beforeinstallprompt`,
 * so feature detection isn't possible — UA sniff is the one
 * legitimate use case here.
 */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ in "Request Desktop Website" mode (which is the
  // DEFAULT for iPad Safari since iPadOS 13). Distinguishable from
  // actual macOS by touch support: macOS reports maxTouchPoints === 0,
  // iPad reports > 0 in every mode.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 0) {
    return true;
  }
  return false;
}

// ─── Singleton `beforeinstallprompt` capture ─────────────────────────

/**
 * Module-level cache for the deferred install prompt event.
 *
 * The browser fires `beforeinstallprompt` exactly once per page load,
 * typically before InstallNudge mounts (the event can fire during
 * initial JS bootstrap, before React hydration completes on the
 * MePage route). If we relied on InstallNudge's own `useEffect`
 * listener to capture it, the event would be missed on slow connections
 * or when the component mounts late (e.g. the user navigates to MePage
 * after first landing on /join).
 *
 * Module load attaches a single window listener that caches the event
 * for the lifetime of the page. The HMR guard prevents duplicate
 * attachment during dev hot-reloads.
 *
 * The cached event also survives MePage unmount/remount cycles —
 * navigating to /settings and back doesn't lose the prompt, because
 * the browser will NOT re-fire `beforeinstallprompt` in the same
 * session even though `display-mode` is still browser (not standalone).
 */
let cachedDeferredPrompt: BeforeInstallPromptEvent | null = null;

interface WindowWithInstallGuard extends Window {
  __ti_beforeInstallPromptAttached?: boolean;
}

if (typeof window !== 'undefined') {
  const w = window as WindowWithInstallGuard;
  if (!w.__ti_beforeInstallPromptAttached) {
    w.__ti_beforeInstallPromptAttached = true;
    w.addEventListener('beforeinstallprompt', ((e: Event) => {
      // Prevent the browser's default mini-infobar (older Chrome) so
      // the install affordance is fully owned by InstallNudge's UI.
      e.preventDefault();
      cachedDeferredPrompt = e as BeforeInstallPromptEvent;
    }) as EventListener);
    // Also watch for `appinstalled` so the singleton clears itself
    // immediately on successful install. The InstallNudge component
    // writes its own dismissal record on the same path; this is
    // defence-in-depth for browsers that fire `appinstalled` without
    // resolving `userChoice.outcome` reliably.
    w.addEventListener('appinstalled', () => {
      cachedDeferredPrompt = null;
    });
  }
}

/**
 * Returns the most recently captured `beforeinstallprompt` event, or
 * null if the browser hasn't fired it (or this is iOS, where the event
 * never fires by design). Consumers MUST call `prompt()` on the event
 * from within a user-gesture handler (e.g. button click) — calling it
 * speculatively will throw or be silently ignored by the browser.
 */
export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return cachedDeferredPrompt;
}

/**
 * Clear the cached prompt. Called by InstallNudge after a successful
 * `prompt()` resolution — the event is single-use; the browser will
 * not honour a second `prompt()` call on the same event object, so
 * holding onto it past first use is just stale state.
 *
 * Also exported for tests, which need to reset the singleton between
 * scenarios.
 */
export function clearDeferredPrompt(): void {
  cachedDeferredPrompt = null;
}

/**
 * Test-only helper to seed the cached prompt without going through
 * a real window event. Not used in production code. Exported so the
 * InstallNudge.test.tsx (when it lands) and any integration tests
 * can simulate the post-event state.
 */
export function _setCachedDeferredPromptForTesting(
  prompt: BeforeInstallPromptEvent | null,
): void {
  cachedDeferredPrompt = prompt;
}
